import {ErrorHandler, LOCALE_ID, NgModule, APP_INITIALIZER} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {RouteReuseStrategy} from '@angular/router';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';

import { IonicRouteStrategy, Platform, provideIonicAngular } from '@ionic/angular';

import {AppComponent} from './app.component';
import {AppRoutingModule} from './app-routing.module';

import {FontAwesomeModule, FaIconLibrary} from '@fortawesome/angular-fontawesome';
import {fas} from '@fortawesome/free-solid-svg-icons'
import {far} from '@fortawesome/free-regular-svg-icons'
import {fab} from '@fortawesome/free-brands-svg-icons'
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {JwtInterceptor} from './helpers/interceptors/jwt.interceptor';
import { IonicStorageModule } from '@ionic/storage-angular';
import {ErrorInterceptor} from './helpers/interceptors/error.interceptor';
import {LocaleInterceptor} from './helpers/interceptors/locale.interceptor';
import {CacheInvalidationInterceptor} from './helpers/interceptors/cache-invalidation.interceptor';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import {provideTranslateService, TranslateService} from '@ngx-translate/core';
import { LanguageService } from './services/language.service';
import { NativeStorage } from '@awesome-cordova-plugins/native-storage/ngx';
import {AirbrakeErrorHandler} from './services/airbrake-error-handler';
import { ApiUrlService } from './services/api-url.service';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { providePrimeNG } from 'primeng/config';
import { MccPrimeNgPreset } from '../theme/mcc-primeng-preset';
import { IonicSharedModule } from './shared/ionic-shared.module';


// Factory function to provide dynamic locale based on language
// Uses centralized locale mapping from LanguageService
export function getLocaleId(translateService: TranslateService): string {
  const currentLang = translateService.getCurrentLang() || translateService.getFallbackLang() || 'de';
  const localeMap = LanguageService.getLocaleMap();
  return localeMap[currentLang] || 'de-DE';
}

// Initialize API URL before app starts to prevent race conditions
export function initializeApp(apiUrlService: ApiUrlService, platform: Platform): () => Promise<any> {
  return () => platform.ready().then(() => apiUrlService.loadApiUrl().toPromise());
}


@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent], imports: [BrowserAnimationsModule,
        BrowserModule,
        IonicSharedModule,
        IonicStorageModule.forRoot(),
        AppRoutingModule,
        FontAwesomeModule,
        ToastModule], providers: [
        MessageService,
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: 'assets/locales/',
                suffix: '.json'
            })
        }),
        provideIonicAngular(),
        providePrimeNG({
            theme: {
                preset: MccPrimeNgPreset,
                options: {
                    darkModeSelector: false
                }
            }
        }),
        { provide: ErrorHandler, useClass: AirbrakeErrorHandler },
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LocaleInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: CacheInvalidationInterceptor, multi: true },
        { provide: LOCALE_ID, useFactory: getLocaleId, deps: [TranslateService] },
        { provide: APP_INITIALIZER, useFactory: initializeApp, deps: [ApiUrlService, Platform], multi: true },
        LanguageService,
        NativeStorage,
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule {
  constructor(library: FaIconLibrary) {
    library.addIconPacks(fas, fab, far);
  }
}